import React from 'react'
import { Link } from 'react-router-dom'
import { PageHeading } from 'app/layout/PageHeading'
import { NeilArmstrongTestimonial } from 'app/layout/Testimonial'
import {
  CLASS_PRICE,
  curriculum,
  memberships,
  startingSwing,
  STORE_URL,
} from '../content/classes'
import erin from 'assets/stlswing/erin-workshop.jpg'

const topics = curriculum.continuingTopics

/** The 6:30 class is always last month's 7:30 topic */
const rotation = topics.map((topic, i) => ({
  month: `Month ${i + 1}`,
  continuing: topic.name,
  advancing: topics[(i + topics.length - 1) % topics.length].advanced,
}))

const firstMonths = [
  {
    month: 'Your first month',
    classes: [{ time: '7:30 PM', name: 'Starting Swing' }],
    note: 'Four Wednesdays that take you from nothing to dancing with anyone in the room.',
  },
  {
    month: 'Your second month',
    classes: [{ time: '7:30 PM', name: 'Continuing Swing' }],
    note: "Come back at the exact same time. You'll join whichever topic is being taught that month.",
  },
  {
    month: 'Every month after',
    classes: [
      { time: '6:30 PM', name: 'Advancing Swing' },
      { time: '7:30 PM', name: 'Continuing Swing' },
    ],
    note: "Go deeper on last month's topic at 6:30, then learn the next one at 7:30. A membership covers both.",
  },
]

const h2 = 'h2'
const lead = 'mt-5 body'

export const CurriculumPage = () => (
  <article>
    <PageHeading
      title='Our curriculum'
      image='getdown/erin-1.jpg'
      position='50% 35%'
      description='How we teach: one night a week, one topic a month, and a social dance after every class so you actually use what you learn.'
    />

    {/* The path a new dancer takes */}
    <section className='max-w-6xl px-4 mx-auto py-14 sm:px-6 md:py-20'>
      <h2 className='h2 h2-lg'>Start any month</h2>
      <p className={lead}>
        Starting Swing begins fresh every month, so there is no wrong time of
        year to begin. From there, everything happens on Wednesday nights.
      </p>

      <ol className='grid gap-6 mt-10 md:grid-cols-3'>
        {firstMonths.map((step) => (
          <li key={step.month} className='p-6 bg-white rounded-3xl'>
            <p className='kicker'>{step.month}</p>
            {step.classes.map((item) => (
              <p key={item.name} className='flex items-baseline mt-3 h3'>
                <span className='w-20 text-sm font-bold text-muted shrink-0'>
                  {item.time}
                </span>
                {item.name}
              </p>
            ))}
            <p className='mt-3 text-muted'>{step.note}</p>
          </li>
        ))}
      </ol>

      <div className='mt-12'>
        <Link to='/starting-swing' className='btn btn-primary'>
          Sign up for Starting Swing
        </Link>
        <p className='mt-3 text-muted'>{startingSwing.nextSeriesText}.</p>
      </div>
    </section>

    {/* Continuing Swing rotation */}
    <section className='text-white bg-night-glow'>
      <div className='max-w-6xl px-4 mx-auto py-14 sm:px-6 md:py-20'>
        <h2 className={h2}>Continuing Swing rotates every month</h2>
        <p className={`${lead} !text-muted-dark`}>
          Continuing Swing is the 7:30 class for anyone who has finished
          Starting Swing. It cycles through three topics, one per month. Jump in
          at any point and you'll have seen all three after three months.
        </p>

        <div className='grid gap-4 mt-10 md:grid-cols-3'>
          {topics.map((topic) => (
            <div
              key={topic.name}
              className='p-6 border border-white/20 rounded-3xl bg-white/5'>
              <h3 className='text-4xl leading-none uppercase font-display font-black text-spot'>
                {topic.name}
              </h3>
              <p className='mt-2 text-muted-dark'>{topic.focus}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Advancing Swing */}
    <section className='max-w-6xl px-4 mx-auto py-14 sm:px-6 md:py-20'>
      <h2 className={h2}>Advancing Swing picks up last month's topic</h2>
      <p className={lead}>
        The 6:30 class is always a continuation of the previous month's 7:30
        class. Take Jumps & Kicks one month, and the next month you can take
        Advanced Jumps & Kicks at 6:30, then stay for the new Continuing Swing
        topic at 7:30.
      </p>

      {/* Cards on phones, a table from tablet up */}
      <ul className='mt-10 space-y-4 md:hidden'>
        {rotation.map((row) => (
          <li key={row.month} className='p-5 bg-white rounded-3xl'>
            <p className='kicker'>{row.month}</p>
            <p className='mt-3 text-sm font-bold text-muted'>
              6:30 PM, Advancing Swing
            </p>
            <p className='h3'>{row.advancing}</p>
            <p className='mt-3 text-sm font-bold text-muted'>
              7:30 PM, Continuing Swing
            </p>
            <p className='h3'>{row.continuing}</p>
          </li>
        ))}
      </ul>
      <table className='hidden w-full mt-10 text-left md:table'>
        <caption className='sr-only'>
          An example of three months of Wednesday classes
        </caption>
        <thead>
          <tr className='border-b-2 border-ink'>
            <th className='py-3 pr-4 kicker !text-ink' scope='col'>
              Wednesdays
            </th>
            <th className='py-3 pr-4 kicker !text-ink' scope='col'>
              6:30 PM, Advancing Swing
            </th>
            <th className='py-3 kicker !text-ink' scope='col'>
              7:30 PM, Continuing Swing
            </th>
          </tr>
        </thead>
        <tbody>
          {rotation.map((row) => (
            <tr key={row.month} className='border-b border-rule'>
              <th className='py-5 pr-4 kicker' scope='row'>
                {row.month}
              </th>
              <td className='py-5 pr-4 h3'>{row.advancing}</td>
              <td className='py-5 h3'>{row.continuing}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className='mt-4 text-muted'>
        Starting Swing also runs at 7:30 PM every month.
      </p>
    </section>

    <NeilArmstrongTestimonial />

    {/* Beyond Wednesdays */}
    <section className='max-w-6xl px-4 mx-auto py-14 sm:px-6 md:py-20 md:grid md:grid-cols-12 md:gap-12 md:items-center'>
      <div className='md:col-span-7'>
        <h2 className={h2}>Then keep going</h2>
        <p className={lead}>
          Once you've taken the full Wednesday curriculum, Erin's 2 hour
          workshops on Tuesdays dig into the hard stuff: musicality, technique,
          and whatever she sees you need.
        </p>
        <p className={lead}>
          We also run electives in other dances, including{' '}
          {curriculum.electives.slice(0, -1).join(', ')} and{' '}
          {curriculum.electives.slice(-1)}.
        </p>
        <a href={`${STORE_URL}/events`} className='inline-block mt-6 link'>
          See what's on the schedule
        </a>
      </div>
      <img
        src={erin}
        alt='Two dancers mid swing out'
        className='object-cover w-full mt-10 h-80 rounded-3xl md:mt-0 md:col-span-5'
      />
    </section>

    {/* Price */}
    <section className='max-w-6xl px-4 pb-14 mx-auto sm:px-6 md:pb-20'>
      <div className='p-6 bg-white rounded-3xl md:p-10'>
        <h2 className={h2}>The dance is always included</h2>
        <p className={lead}>
          Pricing: {CLASS_PRICE}. That social event is Wednesday Night Swing,
          which is $10 on its own. Taking two classes a week? Get the{' '}
          {memberships.everything.name} membership:{' '}
          {memberships.everything.price}.
        </p>
        <div className='flex flex-wrap items-center gap-x-8 gap-y-4 mt-6'>
          <a href={memberships.url} className='btn btn-outline'>
            See memberships
          </a>
          <Link to='/wednesday-night-swing' className='link'>
            About Wednesday Night Swing
          </Link>
        </div>
      </div>
    </section>
  </article>
)

export default CurriculumPage
